/** Saisie d’un paiement manuel (virement, chèque…). */
import { useEffect, useState } from "react";
import { Button } from "../../components/Button";
import { FxDate } from "../../components/FxDate";
import { FxSelect } from "../../components/FxSelect";
import "../../components/confirmModal.css";
import { centsToInput, inputToCents, typedDecimal } from "../catalog/types";
import { useFormatters } from "../../formats/useFormatters";
import { PAY_KINDS } from "../payments/types";
import { todayIso } from "./types";

export type PayDraft = { paid_at: string; amount_cents: number; method: string; reference: string; note: string };

type PayModalProps = {
  open: boolean;
  label: string;
  remainingCents: number;
  decimalComma: boolean;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (p: PayDraft) => void;
};

export function PayModal({ open, label, remainingCents, decimalComma, busy, onCancel, onConfirm }: PayModalProps) {
  const { money } = useFormatters();
  const [paidAt, setPaidAt] = useState(todayIso());
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("transfer");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!open) return;
    setPaidAt(todayIso());
    setAmount(centsToInput(remainingCents, decimalComma));
    setMethod("transfer");
    setReference("");
    setNote("");
  }, [open, remainingCents, decimalComma]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onCancel();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, busy, onCancel]);

  if (!open) return null;

  const cents = inputToCents(amount, decimalComma) ?? 0;
  const surplus = cents - remainingCents;

  return (
    <div className="fx-modal" role="dialog" aria-modal="true" aria-labelledby="fx-pay-title">
      <button type="button" className="fx-modal__backdrop" aria-label="Fermer" disabled={busy} onClick={onCancel} />
      <div className="fx-modal__card">
        <h2 id="fx-pay-title" className="fx-modal__title">
          Marquer comme payée
        </h2>
        <p className="fx-modal__text">
          Paiement reçu pour « {label} ». Le montant proposé est le solde TTC.
        </p>
        <div className="fx-pay-fields">
          <label className="fx-pay-fields__lab">
            Date
            <FxDate value={paidAt} onChange={setPaidAt} />
          </label>
          <label className="fx-pay-fields__lab">
            Montant TTC
            <input className="fx-q-ctrl is-end" value={amount} inputMode="decimal" onChange={(e) => setAmount(typedDecimal(e.target.value, decimalComma))} />
          </label>
          <label className="fx-pay-fields__lab">
            Moyen
            <FxSelect value={method} options={PAY_KINDS} onChange={setMethod} />
          </label>
          <label className="fx-pay-fields__lab">
            Référence
            <input className="fx-q-ctrl" value={reference} placeholder="Réf. virement / transaction…" onChange={(e) => setReference(e.target.value)} />
          </label>
          <label className="fx-pay-fields__lab">
            Notes privées
            <input className="fx-q-ctrl" value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
        </div>
        {surplus > 0 ? (
          <p className="fx-modal__text">
            Surplus de {money(surplus)}. Il reste en trop-perçu sur le client, et s’équilibre au prochain règlement.
          </p>
        ) : null}
        <div className="fx-modal__actions">
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Annuler
          </Button>
          <Button
            disabled={busy || cents <= 0}
            onClick={() => onConfirm({ paid_at: paidAt, amount_cents: cents, method, reference: reference.trim(), note: note.trim() })}
          >
            {busy ? "Enregistrement…" : "Enregistrer le paiement"}
          </Button>
        </div>
      </div>
    </div>
  );
}
